import {
	connectToRouter,
	createBrowserNavigationUrl,
	Navigation,
	NavigationCallback,
	PredefinedPropOptionKind,
	QueryParametersFromProps,
	RouteParametersFromProps
} from "client/Navigation";
import { tryParseInt } from "client/Misc/parseUtils";
import {
	FunctionComponent,
	ReactElement,
	RefObject,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef
} from "react";
import { HubLayoutContext } from "../HubLayout";
import { isNil, isNotNil } from "client/Misc/utils";
import { HubDataSource, HubItemCategoryModel } from "client/Api/Hub/hub";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { hubRouting } from "../routing";
import { AnnouncementsPageContent, AnnouncementsView } from "./AnnouncementsPageContent";
import { useUnavailableCategoryRedirect } from "../useUnavailableCategoryRedirect";
import { useCategories } from "../useCategories";
import { useCanOpenHubManagement } from "../useCanOpenHubManagement";
import {
	HubDialogContext,
	HubDialogContextData,
	ViewSpecificHandlers
} from "../common";
import { useForceRerender } from "client/Hooks";
import { TitleBarBackButton } from "client/Features/Layout/TitleBarBackButton/TitleBarBackButton";
import { HubEvent, publish } from "../events";

export type AnnouncementsPageProps = {
	id?: number;
	categoryId?: number;
	commentId?: number;
	onNavigate: NavigationCallback;
};

export const AnnouncementsPage = (props: AnnouncementsPageProps): ReactElement | null => {
	const {
		id,
		categoryId,
		commentId,
		onNavigate
	} = props;

	const {
		hierarchy,
		hubItemKindsCanCreateNewCategoriesFor,
		canAccessHubManagementForCommunities,
		activeFilter,
		changeActiveFilter,
		changeActiveSidebarItem,
		addOnBeforeNavigateToSidebarItemHandler
	} = useContext(HubLayoutContext);

	const [forceRerender] = useForceRerender();
	const viewSpecificHandlersRef: RefObject<ViewSpecificHandlers | undefined> = useRef<ViewSpecificHandlers>(undefined);

	const categories: OptionalModel<readonly HubItemCategoryModel[]> = useCategories(
		hierarchy,
		HubItemKind.Announcement
	);

	const canOpenHubManagement: boolean = useCanOpenHubManagement(
		categories,
		hubItemKindsCanCreateNewCategoriesFor,
		canAccessHubManagementForCommunities
	);

	useUnavailableCategoryRedirect(
		HubItemKind.Announcement,
		categories,
		categoryId,
		onNavigate
	);

	useEffect(
		(): void => {
			changeActiveSidebarItem({
				source: HubDataSource.Announcement,
				categoryId
			});
		},
		[categoryId, changeActiveSidebarItem]
	);

	const changeViewHandler = useCallback(
		(view: AnnouncementsView): void => {
			const navigation: Navigation = getHubNavigationForAnnouncement({
				source: HubDataSource.Announcement,
				categoryId: categoryId,
				itemId: view.kind === "details"
					? view.id
					: undefined
			});

			onNavigate(navigation);
		},
		[categoryId, onNavigate]
	);

	const selectCategoryHandler = useCallback(
		async (categoryId: number | null): Promise<void> => {
			if (viewSpecificHandlersRef.current?.beforeSwitchingCategoryHandler) {
				const shouldStay: boolean = await viewSpecificHandlersRef.current
					.beforeSwitchingCategoryHandler();

				if (shouldStay) {
					return;
				}
			}

			const navigation: Navigation = getHubNavigationForAnnouncement({
				source: HubDataSource.Announcement,
				categoryId: categoryId ?? undefined,
				itemId: undefined
			});

			onNavigate(navigation);
		},
		[onNavigate]
	);

	const categoryCreatedHandler = useCallback(
		(): void => {
			publish(HubEvent.HierarchyChanged, undefined);
		},
		[]
	);

	const hubDialogContext = useMemo(
		(): HubDialogContextData => {
			return {
				setViewSpecificHandlers: (options): void => {
					viewSpecificHandlersRef.current = options;
					forceRerender();
				}
			};
		},
		[forceRerender]
	);

	useEffect(
		(): (() => void) | void => {
			return addOnBeforeNavigateToSidebarItemHandler(
				async (): Promise<boolean> => {
					const { current: viewSpecificHandlers } = viewSpecificHandlersRef;

					if (!viewSpecificHandlers?.beforeSwitchingCategoryHandler) {
						return false;
					}

					return await viewSpecificHandlers.beforeSwitchingCategoryHandler();
				}
			);
		},
		[addOnBeforeNavigateToSidebarItemHandler]
	);

	const backHandler: (() => void) | undefined = viewSpecificHandlersRef.current
		?.backHandler;

	const view: AnnouncementsView = isNil(id)
		? { kind: "overview" }
		: {
			kind: "details",
			id,
			commentId
		};

	if (!categories.isSet) {
		return null;
	}

	return (
		<>
			{backHandler && (
				<TitleBarBackButton
					onBack={backHandler}
				/>
			)}

			<HubDialogContext.Provider value={hubDialogContext}>
				<AnnouncementsPageContent
					view={view}
					onViewChange={changeViewHandler}
					selectedCategoryId={categoryId ?? null}
					onSelectCategory={selectCategoryHandler}
					categories={categories.value}
					onCategoryCreated={categoryCreatedHandler}
					hubItemKindsCanCreateNewCategoriesFor={hubItemKindsCanCreateNewCategoriesFor}
					activeFilter={activeFilter}
					onActiveFilterChange={changeActiveFilter}
					canOpenHubManagement={canOpenHubManagement}
				/>
			</HubDialogContext.Provider>
		</>
	);
};
AnnouncementsPage.displayName = "AnnouncementsPage";

export const AnnouncementsPageForRouter: FunctionComponent = connectToRouter(
	AnnouncementsPage,
	{
		"id": {
			isRequired: false,
			tryParse: tryParseInt
		},
		"commentId": {
			isRequired: false,
			tryParse: tryParseInt,
			type: "query",
			shouldIgnoreInvalidValues: true
		},
		"categoryId": {
			isRequired: false,
			tryParse: tryParseInt,
			type: "query",
			shouldIgnoreInvalidValues: true
		},
		"onNavigate": PredefinedPropOptionKind.NavigateCallback
	}
);

export type AnnouncementsPageRouteParams = RouteParametersFromProps<
	AnnouncementsPageProps,
	never,
	"id"
>;

export type AnnouncementsPageQueryParams = QueryParametersFromProps<
	AnnouncementsPageProps,
	"categoryId"
	| "commentId"
>;

export type AnnouncementRouterUrlOptions = {
	source: HubDataSource.Announcement;
	categoryId?: number;
} & ({
	itemId: undefined;
} | {
	itemId: number;
	commentId?: number;
});


export function getHubNavigationForAnnouncement(
	options: AnnouncementRouterUrlOptions
): Navigation {
	return hubRouting.announcements({
		routeParams: {
			id: isNotNil(options.itemId)
				? `${options.itemId}`
				: undefined
		},
		queryParams: {
			categoryId: isNotNil(options.categoryId)
				? `${options.categoryId}`
				: undefined,
			commentId: typeof options.itemId === "number"
				&& isNotNil(options.commentId)
					? `${options.commentId}`
					: undefined
		}
	});
}

export function getHubUrlForAnnouncement(
	options: AnnouncementRouterUrlOptions
): string {
	return createBrowserNavigationUrl(
		getHubNavigationForAnnouncement(options)
	);
}
